import { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Pay and payroll permissions (owner spec Pay Rates v1.2, section 10). The server decides every
// action (backend/lib/payAccess.js); this hook only lets a screen hide or disable a control the user
// cannot use. The company admin ticks these per role in Settings, Roles & permissions.
export const PAY_PERMISSION_LABELS = {
  'pay_rates.view': 'Pay Rates: View',
  'pay_rates.edit': 'Pay Rates: Edit',
  'pay_rates.settings': 'Pay Rates: Settings',
  'shift_types.edit': 'Shift Types: Edit',
  'scheduler.edit': 'Roster: Edit',
  'timesheets.approve': 'Timesheets: Approve',
  'timesheets.edit_approved': 'Timesheets: Edit Approved',
  'payroll.send_xero': 'Payroll: Send to Xero',
  'integrations.xero': 'Integrations: Xero',
};
const ALL = Object.keys(PAY_PERMISSION_LABELS);
// What an office user holds until the server answers (the same list as the backend's OFFICE_DEFAULT).
// A user on a custom role holds only that role's ticks, admins and office users included, so every
// role but super admin asks the server; these defaults only fill the moment before it answers.
const OFFICE_DEFAULT = ['pay_rates.view', 'scheduler.edit', 'timesheets.approve', 'timesheets.edit_approved', 'payroll.send_xero', 'integrations.xero'];

// "You need the Pay Rates: Edit permission." for a disabled control's title.
export const needsPermission = (key) => `You need the ${PAY_PERMISSION_LABELS[key] || key} permission. Ask your company admin to add it to your role.`;

// One request per signed-in user, shared by every screen that asks.
let cache = { key: null, promise: null, perms: null };

export default function usePayPermissions() {
  const { user } = useAuth();
  const role = user && user.role;
  const userId = user && user.id;
  const everything = role === 'super_admin';
  const cacheKey = userId ? `${userId}:${role}` : null;
  const fallback = role === 'admin' ? ALL : role === 'office_user' ? OFFICE_DEFAULT : [];
  const [perms, setPerms] = useState(() => (cache.key === cacheKey && cache.perms) || null);

  useEffect(() => {
    if (!cacheKey || everything) return undefined;
    let live = true;
    if (cache.key !== cacheKey || !cache.promise) {
      cache = { key: cacheKey, perms: null, promise: null };
      cache.promise = api.get('/pay-rates/my-permissions')
        .then((r) => {
          const list = r.data && Array.isArray(r.data.permissions) ? r.data.permissions : null;
          if (cache.key === cacheKey) cache.perms = list;
          return list;
        })
        .catch(() => { if (cache.key === cacheKey) cache.promise = null; return null; });
    }
    cache.promise.then((list) => { if (live && list) setPerms(list); });
    return () => { live = false; };
  }, [cacheKey, everything]);

  const list = everything ? ALL : (perms || fallback);
  return { can: (key) => list.includes(key), permissions: list };
}
